import currencyFormatter from 'currency-formatter';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { EModerationStatus, EProductType } from 'v2/interfaces/product';

const addScrollPage = () => {
  document.body.classList.remove('no-scroll');
  document.body.style.paddingRight = '0';
};

const removeScrollPage = () => {
  document.body.classList.add('no-scroll');
  document.body.style.paddingRight = '15px';
};

export const clearMoneyMask = (value: string | number = 0) => {
  value = value.toString();

  if (value.match(/^\d+$/)) {
    value = `${value}.00`;
  }

  if (value.match(/^\d+\.\d+$/)) {
    value = Number(value).toFixed(2);
  }

  value = value.replace(/\D+/gi, '');
  const intval = value.substr(0, value.length - 2);
  const cents = value.substr(value.length - 2, 2);
  value = `${intval}.${cents}`;

  return value;
};

const addMoneyMask = (value: string | number) => {
  if (!value) return value;
  value = value.toString();

  if (value.match(/^\d+$/)) {
    value = `${value}.00`;
  }

  if (value.match(/^\d+\.\d+$/)) {
    value = Number(value).toFixed(2);
  }

  value = value.replace(/\D+/gi, '');
  const intval = value.substr(0, value.length - 2);
  const cents = value.substr(value.length - 2, 2);
  value = `${intval}.${cents}`;
  return currencyFormatter.format(Number(value), { code: 'BRL', precision: 2, symbol: '' });
};

const emailIsValid = (value: string) => {
  if (value) {
    const rgx =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return rgx.test(String(value).toLowerCase()); // true or false
  } else {
    return false;
  }
};

const clearLocalStorage = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('id');
  localStorage.removeItem('name');
  localStorage.removeItem('access_legacy');
  localStorage.removeItem('user_type');
  localStorage.removeItem('photo');
};

const scrollToElement = (element: any, duration?: number) => {
  const startingY = window.pageYOffset;
  const diff = element ? element.getBoundingClientRect().top - startingY : 0;
  let start = 0;

  if (!duration) {
    duration = 2000;
  }

  window.requestAnimationFrame(function step(timestamp) {
    if (!start) start = timestamp;

    const time = timestamp - start;
    const percent = Math.min(time / duration, 1);

    window.scrollTo(0, startingY + diff * percent);

    if (time < duration) {
      window.requestAnimationFrame(step);
    }
  });
};

const getQueryString = (expectedFields: any, filters: any) => {
  let queryParams: string = '';

  if (filters) {
    for (const param in expectedFields) {
      const p = expectedFields[param];
      const fil = filters[param];

      if (fil) {
        if (p.isMultiple) {
          if (Array.isArray(fil)) {
            for (let a = 0; a < fil.length; a++) {
              queryParams += `&${p.apiName}[]=${fil[a].value}`;
            }
          }
        } else {
          if (fil.value || fil.value === false) {
            queryParams += `&${p.apiName}=${fil.value}`;
          } else {
            queryParams += `&${p.apiName}=${fil}`;
          }
        }
      }
    }
  }

  if (queryParams) {
    queryParams = queryParams.substr(1);
  }

  return queryParams || '';
};

const hjTagging = (tags: string[]) => {
  if (typeof window.hj !== 'undefined') {
    window.hj('tagRecording', tags);
  }
};

/**
 * Verifica se o perfil está completo baseado na conta acessada, porém retorna os dados de quem está acessando a conta.
 * @returns {Promise<{ profile: IProfile; hasDocument: boolean }>} Profile: dados de quem está acessando a conta; hasDocument: se o perfil está completo.
 */
export const removeDoubleSpace = (str: string) => str.replace(/ +(?= )/g, '');

export const functions = {
  addScrollPage,
  removeScrollPage,
  clearMoneyMask,
  addMoneyMask,
  emailIsValid,
  clearLocalStorage,
  scrollToElement,
  getQueryString,
  hjTagging,
  cloneDeep,
  isEqual,
  omit,
  removeDoubleSpace
};

export const formatDate = (dateString: string | null): string => {
  if (!dateString) return '';

  const date = new Date(dateString);

  if (isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);

  return `${day}-${month}-${year}`;
};

export const downloadBase64Pdf = (base64: string, filename: string) => {
  if (!base64) {
    console.error('Base64 vazio ou inválido.');
    return;
  }

  let byteCharacters;

  try {
    byteCharacters = atob(base64);
  } catch (error) {
    console.error('Base64 inválido fornecido a downloadBase64Pdf:', error);
    return;
  }

  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: 'application/pdf' });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
};

export function resolveProductStatus(status?: EModerationStatus, productType?: EProductType): EModerationStatus {
  if (productType === EProductType.PHYSICAL && status === EModerationStatus.PENDING) {
    return EModerationStatus.MODERATION;
  }

  return status ?? EModerationStatus.DEFAULT;
}
